import { SortString } from "../../../../helpers/SortString";
import { SkillService } from "../../../../services/SkillService";
import { CountInit } from "../../../Common/PaginationCustom";
import {
  getAssessmentFilterCacheKey,
  resetAssessmentFilterCache,
  resolveAssessmentFilters,
} from "./assessmentFilterCache";

export const getAssessmentRows = (assessmentData) => {
  if (Array.isArray(assessmentData)) return assessmentData;

  const assessmentRows =
    assessmentData?.assessmentList ||
    assessmentData?.assessments ||
    assessmentData?.skillAssessmentList ||
    assessmentData?.assessmentDetailList ||
    assessmentData?.assessmentDetails ||
    assessmentData?.items ||
    assessmentData?.value ||
    [];

  return Array.isArray(assessmentRows) ? assessmentRows : [];
};

export const transformAssessmentList = (
  assessments = [],
  { preserveExistingFields = false } = {}
) =>
  assessments.map((el) => {
    const mappedFields = {
      assessmentType: el?.type?.name || el?.assessmentType || "",
      assessmentSubType: el?.subType?.name || el?.assessmentSubType || "",
      evaluator: el?.evaluatorType?.name || el?.evaluator || "",
    };

    return preserveExistingFields
      ? { ...mappedFields, ...el }
      : { ...el, ...mappedFields };
  });

export const transformAssessmentFilters = (filters = []) => {
  const filterNameMap = {
    question: "assessmentText",
    proficiencyLevel: "levelName",
  };

  return filters.map((filter) => ({
    ...filter,
    name: filterNameMap[filter?.name] || filter?.name,
  }));
};

export const getAssessmentSortBy = (sortBy = "") => {
  const sortByMap = {
    assessmentText: "question",
    levelName: "proficiencyLevel",
  };

  return sortByMap[sortBy] || sortBy;
};

export const transformAssessmentFilterQuery = (filters = "") =>
  filters
    .replaceAll("&assessmentText=", "&question=")
    .replaceAll("&levelName=", "&proficiencyLevel=");

export const getSkillAssessments = async ({
  skillId,
  assessmentRowCount = CountInit,
  assessmentPageOffset = 0,
  assessmentSort = null,
  assessmentFilters = "",
  assessmentLevelCount = 0,
  assessmentFilterCacheRef,
  options = {},
  preserveExistingFields = false,
  onLoadingChange,
  onSuccess,
  onError,
}) => {
  if (!skillId) return null;

  const assessmentFilterCacheKey = getAssessmentFilterCacheKey(
    skillId,
    assessmentLevelCount
  );
  const sortQuery = !!assessmentSort
    ? `&sortBy=${getAssessmentSortBy(assessmentSort?.sortBy) || ""}&sortOrder=${
        assessmentSort?.sortOrder || ""
      }`
    : "";

  if (options?.resetFilterCache) {
    resetAssessmentFilterCache(assessmentFilterCacheRef, assessmentFilterCacheKey);
  }

  if (onLoadingChange) {
    onLoadingChange(true);
  }
  try {
    const skillAssessmentsResponse = await SkillService.getSkillAssessmentsAsyncV4(
      "/" + Number(skillId),
      "/assessments",
      `?pageSize=${assessmentRowCount}`,
      `&offset=${assessmentPageOffset}`,
      sortQuery,
      !!assessmentFilters ? transformAssessmentFilterQuery(assessmentFilters) : "",
      `&proficiencySchemeLevelCount=${assessmentLevelCount}`
    );
    const assessmentFiltersList = resolveAssessmentFilters({
      responseFilters: skillAssessmentsResponse?.filters,
      pageOffset: assessmentPageOffset,
      cacheRef: assessmentFilterCacheRef,
      cacheKey: assessmentFilterCacheKey,
      transformFilters: transformAssessmentFilters,
    });
    const assessmentListMapped = transformAssessmentList(
      getAssessmentRows(skillAssessmentsResponse),
      { preserveExistingFields }
    );
    const sortLevels = !assessmentSort
      ? SortString(assessmentListMapped, "levelName") || []
      : assessmentListMapped;
    const updateData = {
      ...(Array.isArray(skillAssessmentsResponse) ? {} : skillAssessmentsResponse),
      assessmentList: sortLevels,
      filters: assessmentFiltersList,
      total: skillAssessmentsResponse?.totalAssessments ?? sortLevels.length,
    };

    if (onSuccess) {
      onSuccess({
        updateData,
        sortLevels,
        skillAssessmentsResponse,
      });
    }

    return updateData;
  } catch (error) {
    if (onError) {
      onError(error);
    }
    return null;
  } finally {
    if (onLoadingChange) {
      onLoadingChange(false);
    }
  }
};
